/**
 * Google Analytics 4 (GA4) Tracking Utility
 * 
 * HOW TO ACTIVATE:
 * 1. Get your GA4 Measurement ID from Google Analytics (format: G-XXXXXXXXXX)
 * 2. Add the gtag.js script to index.html <head>:
 *    <script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX"></script>
 *    <script>
 *      window.dataLayer = window.dataLayer || [];
 *      function gtag(){dataLayer.push(arguments);}
 *      gtag('js', new Date());
 *      gtag('config', 'G-XXXXXXXXXX');
 *    </script>
 * 3. Replace G-XXXXXXXXXX with your actual Measurement ID
 * 
 * HOW TO VERIFY:
 * - Install "Google Tag Assistant" Chrome extension
 * - Or use GA4 Realtime reports at analytics.google.com
 * - Check browser console for gtag calls (no errors)
 * 
 * EVENTS TRACKED:
 * - page_view (automatic via gtag config)
 * - form_submit (contact form, enquiry form)
 * - whatsapp_click (floating button + inline links)
 * - phone_click (click-to-call)
 * - cta_click (Get Quote, Book Now, etc.)
 * - destination_view (destination page views)
 * - package_view (package page views)
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

type GAEventParams = Record<string, string | number | boolean | undefined>;

const isGtagAvailable = (): boolean => typeof window !== 'undefined' && typeof window.gtag === 'function';

export const trackEvent = (eventName: string, params?: GAEventParams): void => {
  if (isGtagAvailable()) {
    window.gtag!('event', eventName, params);
  }
};

export const trackPageView = (path: string, title?: string): void => {
  if (isGtagAvailable()) {
    window.gtag!('event', 'page_view', {
      page_path: path,
      page_title: title,
    });
  }
};

export const trackFormSubmit = (formName: string, destination?: string): void => {
  trackEvent('form_submit', { form_name: formName, destination });
};

export const trackWhatsAppClick = (source: string): void => {
  trackEvent('whatsapp_click', { source });
};

export const trackPhoneClick = (source: string): void => {
  trackEvent('phone_click', { source });
};

export const trackCTAClick = (buttonLabel: string, page: string): void => {
  trackEvent('cta_click', { button_label: buttonLabel, page });
};

export const trackDestinationView = (destination: string): void => {
  trackEvent('destination_view', { destination });
};

export const trackPackageView = (packageName: string): void => {
  trackEvent('package_view', { package_name: packageName });
};
